import { Trans } from '@lingui/macro';
import type { BackendTypes } from '@tf/api';
import type { ExtendedFormDefinition } from '@tf/shared';
import { extractForms, getSegmentsForReview, TFPageContent } from '@tf/shared';
import { Button, Center, Icon, TFCard, TFEmptyStateMessage, TFText } from '@tf/ui';
import { T } from '@tf/utils';
import constate from 'constate';
import type React from 'react';
import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';

import type { Application } from '@/core/api';
import { useApplicationQuery, useApplicationSharingStatus } from '@/core/api';
import { useDefinitions } from '@/core/providers';
import { useApplicationNodeIdParam } from '@/routes/endClientArea/params';
import { endClientAreaPaths } from '@/routes/endClientArea/paths';

type State = {
	application: Application;
	forms: ExtendedFormDefinition<BackendTypes.FormDefinition>[];
};

const getMainReview = (application: Application) => {
	const mainReview = application.reviews.find((r) => r.connectionKind === 'APPLICANT');
	T.assertNotNullish(mainReview, 'no applicant review found');

	return mainReview;
};

const [ApplicationStateProvider, useApplicationState, useApplicationFormsState, useApplicationMainReviewState] =
	constate(
		({ application }: { application: Application }): State => {
			const definitions = useDefinitions();

			const forms = useMemo(() => {
				const mainReview = getMainReview(application);

				const segmentIdentities = getSegmentsForReview(application.account, mainReview).filter((c) => {
					return [application.account.graphNodeId, mainReview.graphNodeId, mainReview.graphLinkId].includes(c.graphId);
				});

				const segmentFilters =
					definitions.defs.accounts?.[application.account.entityKind]?.connections?.[mainReview.connectionKind]
						?.segment_filters;

				const mainEntity = mainReview.listEntities.find((e) => e.graphNodeId === mainReview.graphNodeId);

				const blockedSegments =
					segmentFilters && mainEntity ? new Set(segmentFilters[mainEntity.entityKind] ?? []) : new Set<string>();

				const filteredSegments =
					blockedSegments.size > 0
						? segmentIdentities.filter((s) => !blockedSegments.has(s.segmentKind))
						: segmentIdentities;

				return extractForms({
					formDefs: definitions.defs.forms,
					segmentIdentities: filteredSegments,
				});
			}, [application, definitions.defs.forms, definitions.defs.accounts]);

			return { application, forms };
		},
		(state) => state.application,
		(state) => state.forms,
		(state) => getMainReview(state.application),
	);

export const useApplication = useApplicationState;

export const useApplicationForms = useApplicationFormsState;

export const useApplicationMainReview = useApplicationMainReviewState;

export const ApplicationProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const applicationNodeId = useApplicationNodeIdParam();

	const applicationQuery = useApplicationQuery(applicationNodeId);
	const shareJob = useApplicationSharingStatus(applicationNodeId);

	if (shareJob.data?.status === 'SHARED') {
		return <Navigate to={endClientAreaPaths.applicationSubmitted()} />;
	}

	if (applicationQuery.data && shareJob.data) {
		return <ApplicationStateProvider application={applicationQuery.data}>{children}</ApplicationStateProvider>;
	}

	return (
		<TFPageContent w="50rem" maw="50rem" m="0">
			<Center h="calc(100vh - 70px - 2rem)">
				<TFCard innerPadding="1rem 3rem 1rem" w="100%" maw={814}>
					{applicationQuery.error ? (
						<TFEmptyStateMessage
							icon={<Icon.IconSearchOff />}
							title="Application not found"
							description="Please start from homepage to create new application"
							primaryAction={
								<Button
									leftSection={<Icon.IconHome size={24} />}
									variant="filled"
									component="a"
									size="md"
									h={48}
									fz={16}
									fw={500}
									href={endClientAreaPaths.startApplication()}
								>
									Go to homepage
								</Button>
							}
						/>
					) : (
						<TFText>
							<Trans id="formLoadingCard.loading">Loading...</Trans>
						</TFText>
					)}
				</TFCard>
			</Center>
		</TFPageContent>
	);
};
